import dayjs from "dayjs";
import db from "@/db";
export const CONFIDENT_MATCH_DELTA_MINUTES = 10;

export const NIGHT_MATCH_WINDOW_HOURS = 6;

const isSameInterventionDay = (a, b) =>
  a.isSame(b, "day") ||
  Math.abs(a.diff(b, "hour", true)) <= NIGHT_MATCH_WINDOW_HOURS;

export const toIsoDate = (dateStr) => {
  if (!dateStr) return null;
  const [datePart] = String(dateStr).split(/[T ]/);
  if (!datePart.includes("/")) return datePart;
  const [day, month, year] = datePart.split("/");
  return `${year}-${month}-${day}`;
};

export default function useRecordBookMapper() {
  const getTrials = (speedBook, index) => {
    if (!speedBook || !speedBook.recordForm?.trials?.length) {
      return [];
    }
    const trials = speedBook.recordForm.trials.map((t, index) => {
      const start = dayjs(t.startTime).format("HH:mm");
      const end = dayjs(t.endTime).format("HH:mm");
      return {
        index: index,
        label: `Essai ${index + 1}: ${start}-${end}`,
      };
    });
    if (index > -1) {
      return trials.find((t) => t.index === index);
    }
    return trials;
  };
  const makeDatetime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) {
      return null;
    }
    return dayjs(`${toIsoDate(dateStr)} ${timeStr}`);
  };

  const getHumidityMeasurementTime = async (book) => {
    const envBook = await db.recordBooks.get({
      uuid: book.environmentalCondition,
    });
    // G.R 23/07/26 cahier de condition environnementale obsolète/supprimé : on
    // renvoie null (pas de correspondance) au lieu de planter la recherche.
    if (!envBook) return null;
    const date = envBook.recordForm.interventionDate;
    if (book.measurementMethod == "SONDE_CAPACITIVE") {
      return makeDatetime(date, book.recordForm.measurementHour);
    }
    if (book.measurementMethod == "GRAVIMETRIE_HUMIDITE") {
      const firstSample = book.recordForm.measurements?.find(
        (m) => m.type == "FIRST"
      );
      if (firstSample) {
        return makeDatetime(date, firstSample.hour);
      }
      return null;
    }
    if (book.measurementMethod == "GRAVIMETRIE_PARAMETRE") {
      const firstSample = book.recordForm.measurements?.[0];
      if (firstSample) {
        return makeDatetime(firstSample.date, firstSample.hour);
      }
      return null;
    }
  };

  const getHumidityMeasurementEndHour = (book) => {
    const measurements = book.recordForm?.measurements ?? [];
    let lastSample = null;
    if (book.measurementMethod == "GRAVIMETRIE_HUMIDITE")
      lastSample = measurements.find((m) => m.type == "LAST");
    if (book.measurementMethod == "GRAVIMETRIE_PARAMETRE")
      lastSample = measurements.at(-1);
    return typeof lastSample?.hour === "string"
      ? lastSample.hour.slice(0, 5)
      : null;
  };

  const findHumidityRecordBookByTime = async (date, time, books) => {
    const dateTime = makeDatetime(date, time);
    if (!dateTime?.isValid()) return null;

    let closestBook = null;
    let smallestDelta = Infinity;
    for (const book of books) {
      const recordBookDatetime = await getHumidityMeasurementTime(book);
      if (!recordBookDatetime?.isValid()) continue;
      if (!isSameInterventionDay(recordBookDatetime, dateTime)) continue;
      const delta = Math.abs(recordBookDatetime.diff(dateTime, "minute"));
      if (delta < smallestDelta) {
        smallestDelta = delta;
        closestBook = {
          uuid: book.uuid,
          label: book.label,
          matchDeltaMinutes: delta,
        };
      }
    }
    return closestBook;
  };
  const findSpeedRecordBookByTime = (date, time, books) => {
    const dateTime = makeDatetime(date, time);
    if (!dateTime?.isValid()) return [null, null, null];

    let closestTrial = [null, null, null];
    let smallestDelta = Infinity;
    for (const book of books) {
      for (const [index, trial] of (book.recordForm?.trials ?? []).entries()) {
        const trialDatetime = dayjs(trial.startTime);
        if (!trialDatetime.isValid()) continue;
        if (!isSameInterventionDay(trialDatetime, dateTime)) continue;
        const delta = Math.abs(trialDatetime.diff(dateTime, "minute"));
        if (delta < smallestDelta) {
          smallestDelta = delta;
          closestTrial = [index, book, delta];
        }
      }
    }
    return closestTrial;
  };
  const getHumidityMatchDelta = async (date, time, book) => {
    const dateTime = makeDatetime(date, time);
    const measurementTime = await getHumidityMeasurementTime(book);
    if (!dateTime?.isValid() || !measurementTime?.isValid()) return null;
    return Math.abs(measurementTime.diff(dateTime, "minute"));
  };

  const sortSamplingRecordBooksByStartDate = (samplingRecordList) => {
    return samplingRecordList
      .filter((recordBook) => !recordBook.isArchived)
      .map((recordBook) => {
        let startDate = null;
        if (recordBook.recordForm?.measurementForm?.measures) {
          const measures = recordBook.recordForm.measurementForm.measures;
          const allInitialMeasures = Object.values(measures)
            .map((m) => m.mesureInitiale)
            .filter((m) => m?.date);

          if (allInitialMeasures.length > 0) {
            startDate = new Date(
              Math.min(
                ...allInitialMeasures.map((m) => new Date(m.date).getTime())
              )
            );
          }
        }

        return {
          recordBook,
          startDate,
        };
      })
      .sort((a, b) => {
        if (!a.startDate && !b.startDate) return 0;
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        return a.startDate - b.startDate;
      });
  };

  return {
    findHumidityRecordBookByTime,
    findSpeedRecordBookByTime,
    getHumidityMatchDelta,
    getHumidityMeasurementTime,
    getHumidityMeasurementEndHour,
    sortSamplingRecordBooksByStartDate,
    getTrials,
  };
}
